import { useContext, useEffect, useMemo, useRef } from 'react';
import {
  StatsigProvider as StatsigProviderCore,
  useClientAsyncInit,
  type StatsigUser,
} from '@statsig/react-bindings';
import type { OverrideAdapter } from '@statsig/client-core';
import { useAuthUser } from './hooks';
import { UserContext } from './contexts';
import { LoadingSpinner } from './components';

// In test mode, enable all feature gates so E2E tests can access gated features.
// Individual tests that need to verify "gate off" behavior should mock useFeatureGate directly.
// All methods on OverrideAdapter are optional — only getGateOverride is needed.
// See: https://docs.statsig.com/client/javascript-sdk/overrides
const testOverrideAdapter: OverrideAdapter = {
  getGateOverride: (gate) => ({ ...gate, value: true }),
};

const StatsigProvider = ({ user, children }: { user: StatsigUser; children: React.ReactNode }) => {
  const isTestMode = import.meta.env.MODE === 'test';
  const { client, isLoading } = useClientAsyncInit(import.meta.env.VITE_STATSIG_SDK_KEY, user, {
    networkConfig: {
      preventAllNetworkTraffic: isTestMode,
    },
    environment: {
      tier:
        import.meta.env.MODE === 'production'
          ? 'production'
          : import.meta.env.MODE === 'staging'
          ? 'staging'
          : 'development',
    },
    ...(isTestMode && { overrideAdapter: testOverrideAdapter }),
  });

  // Re-evaluate gates when user data changes (e.g., clientId becomes available)
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!isLoading) {
      if (initializedRef.current && user.userID) {
        client.updateUserAsync(user);
      }
      initializedRef.current = true;
    }
  }, [client, isLoading, user]);

  // Show loading spinner only when we have a real user to wait for.
  // When user data isn't ready yet (empty user), render children immediately
  // so the app isn't blocked — gates will return default values (false).
  if (isLoading && user.userID) {
    return <LoadingSpinner />;
  }

  return <StatsigProviderCore client={client}>{children}</StatsigProviderCore>;
};

const Wrapper = ({ children }: { children: React.ReactNode }) => {
  const { name, user: authUser, clientId } = useAuthUser();
  const { roles, isSuperUser } = useContext(UserContext);
  const user: StatsigUser = useMemo(
    () =>
      authUser && name && clientId
        ? {
            email: authUser.email,
            appVersion: import.meta.env.VITE_APP_GIT_SHA,
            userID: name,
            custom: {
              roles,
              superUser: isSuperUser,
              clientId,
            },
            customIDs: {
              clientId,
            },
          }
        : {},
    [authUser, name, clientId, roles, isSuperUser],
  );

  return <StatsigProvider user={user}>{children}</StatsigProvider>;
};

export default Wrapper;
